import BlankFieldSettings from "../../common/BlankFieldSettings.tsx";
import { Divider, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch";
import {
  selectTabularFields,
  selectTabularGrouping,
  selectTabularSettingsState,
} from "../../../../store/selectors/fieldsStateSelectors.ts";
import { fieldsStateActions } from "../../../../store/thunks/fieldStatesThunks.ts";
import { calculateShowNonZeroBalanceAccounts } from "../../../../store/utils/calculateShowNonZeroBalanceAccounts.ts";
import CheckboxField from "../../../common/CheckboxField.tsx";
import SwitchField from "../../../common/SwitchField.tsx";
import GroupSummaryPlace from "./GroupSummaryPlace.tsx";
import SubtotalLabelContainer from "./subtotal-label/SubtotalLabelContainer.tsx";

interface Props {
  isApiReport: boolean;
  readonlyMode: boolean;
}
export default function GeneralSettings({ isApiReport, readonlyMode }: Props) {
  const settingsAreaValues = useSelector(selectTabularSettingsState);
  const fieldsAreaValues = useSelector(selectTabularFields);
  const groups = useSelector(selectTabularGrouping);
  const hasGroups = groups.length > 0;
  const isGroupedColumnsDisabled = !hasGroups;
  const isSubtotalLabelDisabled = settingsAreaValues.groupSummaryPlace === "Inline" || !hasGroups;
  const hideBlankRows = settingsAreaValues.hideBlankRows === true;
  const dispatch = useAppDispatch();

  const showNonZeroBalanceAccounts = useMemo(
    () => calculateShowNonZeroBalanceAccounts(fieldsAreaValues),
    [fieldsAreaValues]
  );

  return (
    <Stack spacing={1.5}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle1">General</Typography>
        <Stack sx={{ gap: 0.7 }}>
          {showNonZeroBalanceAccounts && (
            <SwitchField
              label="Show Non-Zero Balance Accounts"
              checked={settingsAreaValues.showNonZeroBalanceAccounts === true}
              onChange={(value) =>
                dispatch(fieldsStateActions.updateSettings({ settings: { showNonZeroBalanceAccounts: value } }))
              }
              disabled={readonlyMode}
            />
          )}
          {!isApiReport && (
            <SwitchField
              label="Show Grouped Columns"
              checked={settingsAreaValues.showGroupedColumns === true}
              onChange={(value) =>
                dispatch(fieldsStateActions.updateSettings({ settings: { showGroupedColumns: value } }))
              }
              disabled={isGroupedColumnsDisabled || readonlyMode}
            />
          )}
          <SwitchField
            label="Hide Blank Rows"
            checked={hideBlankRows}
            onChange={(hideBlankRows) => {
              const resetHideZeroRows = hideBlankRows === false ? false : settingsAreaValues.hideZeroRows;
              dispatch(
                fieldsStateActions.updateSettings({
                  settings: {
                    hideBlankRows,
                    hideZeroRows: resetHideZeroRows,
                  },
                })
              );
            }}
            tooltipText="Hides rows in which all measure values are empty."
            disabled={readonlyMode}
          />
          {hideBlankRows && (
            <CheckboxField
              checked={settingsAreaValues.hideZeroRows === true}
              onChange={(value) => dispatch(fieldsStateActions.updateSettings({ settings: { hideZeroRows: value } }))}
              label="Treat Zeros as Empty"
              formControlLabelSx={{ ml: 1.25 }}
              disabled={readonlyMode}
            />
          )}
          <BlankFieldSettings
            fields={fieldsAreaValues}
            settings={{
              hideBlank: settingsAreaValues.hideBlankColumns,
              hideZero: settingsAreaValues.hideZeroColumns,
              scope: settingsAreaValues.hideBlankColumnsScope,
              fieldIds: settingsAreaValues.hideBlankColumnIds,
            }}
            readonlyMode={readonlyMode}
            onChange={({ hideBlank, hideZero, scope, fieldIds }) =>
              dispatch(
                fieldsStateActions.updateSettings({
                  settings: {
                    hideBlankColumns: hideBlank,
                    hideZeroColumns: hideZero,
                    hideBlankColumnsScope: scope,
                    hideBlankColumnIds: fieldIds,
                  },
                })
              )
            }
          />
        </Stack>
      </Stack>
      <Divider sx={{ borderStyle: "dashed" }} />
      <Typography variant="subtitle1">Totals</Typography>
      <SwitchField
        label="Show Grand Total"
        checked={settingsAreaValues.showGrandTotal === true}
        onChange={(showGrandTotal) => dispatch(fieldsStateActions.updateSettings({ settings: { showGrandTotal } }))}
        disabled={readonlyMode}
      />
      <GroupSummaryPlace
        value={settingsAreaValues.groupSummaryPlace}
        onChange={(groupSummaryPlace) =>
          dispatch(fieldsStateActions.updateSettings({ settings: { groupSummaryPlace } }))
        }
        disabled={!hasGroups || readonlyMode}
      />
      <SubtotalLabelContainer disabled={isSubtotalLabelDisabled || readonlyMode} />
    </Stack>
  );
}
